import { API_CONFIG } from '../config/api';
import type { 
  Brand, 
  CreateBrandData, 
  UpdateBrandData
  // Removed BrandsResponse and BrandResponse
} from '../types/brand';

// Get all brands
export const getAllBrands = async (): Promise<Brand[]> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/brands`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch brands:', response.status, errorText);
      throw new Error(`Failed to fetch brands: ${response.status}`);
    }

    const result = await response.json();
    
    // Handle different response formats
    if (result.data && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching brands:', error);
    throw new Error('Failed to fetch brands');
  }
};

// Create a new brand
export const createBrand = async (brandData: CreateBrandData): Promise<Brand> => {
  try {
    console.log('Creating brand with data:', brandData);

    const response = await fetch(`${API_CONFIG.BASE_URL}/brands`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(brandData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Brand creation failed:', response.status, errorText);
      
      let errorMessage = 'Failed to create brand';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Brand created successfully:', result);
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error creating brand:', error);
    throw error;
  }
};

// Update a brand
export const updateBrand = async (brandData: UpdateBrandData): Promise<Brand> => {
  try {
    console.log('Updating brand with data:', brandData);

    const response = await fetch(`${API_CONFIG.BASE_URL}/brands/${brandData.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({
        name: brandData.name,
        description: brandData.description,
        status: brandData.status,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Brand update failed:', response.status, errorText);
      
      let errorMessage = 'Failed to update brand';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Brand updated successfully:', result);
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error updating brand:', error);
    throw error;
  }
};

// Delete a brand
export const deleteBrand = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/brands/${id}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Brand deletion failed:', response.status, errorText);
      
      let errorMessage = 'Failed to delete brand';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    console.log('Brand deleted successfully');
  } catch (error) {
    console.error('Error deleting brand:', error);
    throw error;
  }
};